// fulcalendar関係のutiles
// fullcalendarのデータとして、TGanttResource, TGanttEventの利用する
import { ResourceApi } from '@fullcalendar/resource-common';
import {
  SITE_PROCESS_STATUS_COMPLETED,
  SITE_PROCESS_STATUS_UNNECESSARY
} from '../const/Const';
import {
  GANTT_DISPLAY_MAX_YEAR,
  GANTT_ORDER_ZERO_FILL_LENGTH
} from '../const/ProcessConst';
import { TConstruction } from '../types/common';
import {
  TGanttEvent,
  TGanttResource,
  TGanttVisibleRange,
  TSiteProcessListData
} from '../types/process';
import { convertFormDateToApiDate } from './convertDateFormat';
import { getConstructionsForSiteProcessCopy } from '../apicalls/process';
import { TSelectItem } from '../types/common';
import { checkResData } from './common';
import { OptionsObject, SnackbarKey, SnackbarMessage } from 'notistack';

// yyyy/mm/ddをfullcalendarで扱う形式(yyyy-mm-dd)に変換する処理
export const convertToFullCalendarDateString = (input: string) => {
  const output = input && input.replaceAll('/', '-');

  return output;
};

// 工程の終了日をガントチャート用の日付に変換する。ガントチャートでは前日までしか塗られないので当日まで塗られるようにする。
export const convertToFullCalendarEndDateString = (input?: string | null) => {
  if (!input || input === '') {
    return undefined;
  }

  const endDate = new Date(input);
  const ganttEventEndYear = endDate.getFullYear();
  const ganttEventEndMonth = (endDate.getMonth() + 1)
    .toString()
    .padStart(2, '0');
  const ganttEventEndDate = (endDate.getDate() + 1).toString().padStart(2, '0');
  const output = convertToFullCalendarDateString(
    `${ganttEventEndYear}/${ganttEventEndMonth}/${ganttEventEndDate}`
  );

  return output;
};

// ガントのリソース（工程）データから一番早い開始日と一番遅い終了日を探し表示用の日付とガントチャート用の表示範囲を返す
// 開始日と終了日が指定期間以上の場合は、終了日を指定期間内にする
export const getSiteProcessVisibleRange = (
  construction: TConstruction | undefined,
  resourceList: Array<TGanttResource> | Array<ResourceApi>
): { start: string; end: string; visibleRange: TGanttVisibleRange } => {
  // 工程データがある場合は工程から表示期間を求める
  let firstStartDate = '9999/12/31';
  if (construction?.start_date) {
    firstStartDate = convertFormDateToApiDate(
      construction.start_date
    ) as string;
  } else if (resourceList.length === 0) {
    // 工事に着工日がなく工程がない場合は期間なし
    firstStartDate = '';
  }
  let lastEndDate = '';
  if (construction?.end_date) {
    lastEndDate = convertFormDateToApiDate(construction.end_date) as string;
  }
  resourceList.forEach((resource: TGanttResource | ResourceApi) => {
    const siteProcess = resource.extendedProps.siteProcess;
    const endDate = siteProcess.end_date || siteProcess.start_date;
    if (firstStartDate > siteProcess.start_date) {
      firstStartDate = siteProcess.start_date;
    }
    if (lastEndDate < endDate) {
      lastEndDate = endDate;
    }
  });

  if (lastEndDate) {
    // 開始日と終了日が指定期間以上の場合は、終了日を指定期間内にする
    if (!validateGanttPeriod(firstStartDate, lastEndDate)) {
      const lastDate = new Date(firstStartDate);
      lastDate.setFullYear(lastDate.getFullYear() + GANTT_DISPLAY_MAX_YEAR);
      const year = lastDate.getFullYear().toString().padStart(4, '0');
      const month = (lastDate.getMonth() + 1).toString().padStart(2, '0');
      const date = lastDate.getDate().toString().padStart(2, '0');
      lastEndDate = `${year}/${month}/${date}`;
    }
  } else {
    // 終了日がない場合は開始日をいれる
    lastEndDate = firstStartDate;
  }

  return {
    start: convertToFullCalendarDateString(firstStartDate),
    end: convertToFullCalendarDateString(lastEndDate),
    visibleRange: {
      start: convertToFullCalendarDateString(firstStartDate),
      end: lastEndDate
        ? convertToFullCalendarEndDateString(lastEndDate)
        : convertToFullCalendarEndDateString(firstStartDate)
    }
  };
};

export const fetchConstructList = async (
  enqueueSnackbar: (
    message: SnackbarMessage,
    options?: OptionsObject | undefined
  ) => SnackbarKey,
  withEmptyOption: boolean = false
): Promise<TSelectItem[]> => {
  const resData = await getConstructionsForSiteProcessCopy();
  const data = await checkResData(resData, enqueueSnackbar);
  let list: TSelectItem[] = [];
  if (data) {
    list = data.data.map((item: { id: number; name: string }) => ({
      value: item.id,
      label: item.name ?? ''
    }));
    if (withEmptyOption) {
      list.unshift({ value: 0, label: '--' });
    }
  }
  return list;
};

// ガントのリソース（工程）データから一番早い開始日と一番遅い終了日を探し表示用の日付とガントチャート用の表示範囲を返す
// 開始日と終了日が指定期間以上の場合は、終了日を指定期間内にする
export const getAttendanceManageVisibleRange = (
  construction: TConstruction | undefined,
  resourceList: Array<TGanttResource> | Array<ResourceApi>
): { start: string; end: string; visibleRange: TGanttVisibleRange } => {
  // 工程データがある場合は工程から表示期間を求める
  let firstStartDate = '9999/12/31';
  if (construction?.start_date) {
    firstStartDate = convertFormDateToApiDate(
      construction.start_date
    ) as string;
  } else if (resourceList.length === 0) {
    // 工事に着工日がなく工程がない場合は期間なし
    firstStartDate = '';
  }
  let lastEndDate = '';
  if (construction?.end_date) {
    lastEndDate = convertFormDateToApiDate(construction.end_date) as string;
  }
  resourceList.forEach((resource: TGanttResource | ResourceApi) => {
    const siteProcess = resource.extendedProps.siteProcess;
    const endDate = siteProcess.end_date || siteProcess.start_date;
    if (firstStartDate > siteProcess.start_date) {
      firstStartDate = siteProcess.start_date;
    }
    if (lastEndDate < endDate) {
      lastEndDate = endDate;
    }
  });

  if (lastEndDate) {
    // 開始日と終了日が指定期間以上の場合は、終了日を指定期間内にする
    if (!validateGanttPeriod(firstStartDate, lastEndDate)) {
      const lastDate = new Date(firstStartDate);
      lastDate.setFullYear(lastDate.getFullYear() + GANTT_DISPLAY_MAX_YEAR);
      const year = lastDate.getFullYear().toString().padStart(4, '0');
      const month = (lastDate.getMonth() + 1).toString().padStart(2, '0');
      const date = lastDate.getDate().toString().padStart(2, '0');
      lastEndDate = `${year}/${month}/${date}`;
    }
  } else {
    // 終了日がない場合は開始日をいれる
    lastEndDate = firstStartDate;
  }

  return {
    start: convertToFullCalendarDateString(firstStartDate),
    end: convertToFullCalendarDateString(lastEndDate),
    visibleRange: {
      start: convertToFullCalendarDateString(firstStartDate),
      end: lastEndDate
        ? convertToFullCalendarEndDateString(lastEndDate)
        : convertToFullCalendarEndDateString(firstStartDate)
    }
  };
};

// 工程データのidをfullcalendarが扱う文字列のidに変更する。
export const convertToFullCalendarId = (id: number) => {
  return id.toString();
};

// 工程データに合わせたガントチャートのバーの背景色を取得する
export const getEventBackgroundColor = (siteProcess: TSiteProcessListData) => {
  if (siteProcess.status === SITE_PROCESS_STATUS_COMPLETED) {
    return '#808080';
  }
  if (siteProcess.status === SITE_PROCESS_STATUS_UNNECESSARY) {
    return '#f5f5f5';
  }
  if (siteProcess.parent_site_process_id) {
    return '#fcc';
  }

  return '#fc0';
};

// 工程データに合わせたガントチャートの文字色を取得する
export const getEventTextColor = (siteProcess: TSiteProcessListData) => {
  if (siteProcess.status === SITE_PROCESS_STATUS_COMPLETED) {
    return 'white';
  }
  if (siteProcess.status === SITE_PROCESS_STATUS_UNNECESSARY) {
    return '#808080';
  }

  return 'black';
};

// 工程の並び順を作成する。バックとロジックを合わせる。
// 工程の並び順は文字列のため数字の文字列でソートができるように数字を0パディングする。
// 例）
// 1.仮設工事, 2.軽鉄・ボード工事, 10. 塗装工事とあった場合
// 1.仮設工事, 10.塗装工事, 2.軽鉄・ボード工事,と並んでしまうため
// 数字を0パディングし、以下のようにならぶようにする
// 0001.仮設工事, 0002.軽鉄・ボード工事, 0010.塗装工事
//
// なお工程名にある数字は全て0パディングする
// 例）1.仮設2のクリーニング1 0001.仮設0002のクリーニング0001
export const getOrder = (siteProcess: TSiteProcessListData): string => {
  const order = createOrderName(siteProcess.site_process_name);

  return order;
};

// ソート用の名前を作成する
export const createOrderName = (name?: string | null) => {
  if (!name) {
    return '';
  }

  const orderName = name.replace(/(\d+)/g, target =>
    target.padStart(GANTT_ORDER_ZERO_FILL_LENGTH, '0')
  );

  return orderName;
};

// 工程データをガントチャートのresourceデータに変換する処理
export const createGanttResource = (
  siteProcessData: TSiteProcessListData,
  allSiteTaskCount?: number,
  allAlertSiteTaskCount?: number,
  isChildOpen?: boolean,
  construct?: { id: number; name: string } | null
): TGanttResource => {
  const ganttResource: TGanttResource = {
    id: convertToFullCalendarId(siteProcessData.id),
    title: siteProcessData.site_process_name,
    orderId: siteProcessData.order_id,
    extendedProps: {
      order: getOrder(siteProcessData),
      siteProcess: siteProcessData,
      allSiteTaskCount: allSiteTaskCount
        ? allSiteTaskCount
        : siteProcessData.site_task_count,
      allAlertSiteTaskCount: allAlertSiteTaskCount
        ? allAlertSiteTaskCount
        : siteProcessData.alert_count,
      isChildOpen: isChildOpen === undefined ? false : isChildOpen,
      constructOrder: createOrderName(construct?.name),
      construct: construct || null,
      delete: false
    }
  };

  // 大項目の設定
  if (siteProcessData.parent_site_process_id) {
    ganttResource.parentId = convertToFullCalendarId(
      siteProcessData.parent_site_process_id
    );
  }

  return ganttResource;
};

export const createGanttResourceAttendanceManage = (
  attendanceData: any,
  isChildOpen?: boolean,
  construct?: { id: number; name: string } | null
): TGanttResource => {
  let totalling = 0;
  attendanceData.attendance_manage_event_list.map(
    (i: any) => (totalling += i.artificial)
  );
  const ganttResource: any = {
    id: convertToFullCalendarId(attendanceData.id),
    title:
      attendanceData.business_type == 1
        ? attendanceData.user_name
        : attendanceData.partner_name,
    totalling: Math.floor(totalling * 10) / 10,
    extendedProps: {
      attendance: attendanceData,
      allSiteTaskCount: 0,
      allAlertSiteTaskCount: 0,
      isChildOpen: isChildOpen === undefined ? false : isChildOpen,
      constructOrder: createOrderName(construct?.name),
      construct: construct || null,
      delete: false
    }
  };

  // 大項目の設定
  if (attendanceData.parent_site_process_id) {
    ganttResource.parentId = convertToFullCalendarId(
      attendanceData.parent_site_process_id
    );
  }

  return ganttResource;
};

// 工程データをガントチャートのeventデータに変換する処理
export const createGanttEvent = (
  siteProcessData: any,
  addParentResource: boolean = true,
  addResource: boolean = true
): TGanttEvent => {
  const resourceIds: Array<string> = [];
  if (addResource) {
    resourceIds.push(convertToFullCalendarId(siteProcessData.id));
  }
  if (addParentResource && siteProcessData.parent_site_process_id) {
    resourceIds.push(
      convertToFullCalendarId(siteProcessData.parent_site_process_id)
    );
  }

  const ganttEvent: TGanttEvent = {
    id: convertToFullCalendarId(siteProcessData.id),
    title: siteProcessData.site_process_name,
    start: convertToFullCalendarDateString(siteProcessData.start_date),
    end: convertToFullCalendarEndDateString(siteProcessData.end_date),
    resourceIds,
    extendedProps: {
      order: getOrder(siteProcessData),
      priorityOrder: siteProcessData.parent_site_process_id ? 1 : 0,
      siteProcess: siteProcessData
    }
  };

  // 終了日がある場合
  if (siteProcessData.end_date) {
    const endDate = new Date(siteProcessData.end_date);
    // カレンダーでは色が前日までしか塗られないので当日まで塗られるようにする。
    // 日付はyyyy/mm/ddの形として最後にfullcalenarが扱うyyyy-mm-ddにする。
    const ganttEventEndYear = endDate.getFullYear();
    const ganttEventEndMonth = (endDate.getMonth() + 1)
      .toString()
      .padStart(2, '0');
    const ganttEventEndDate = (endDate.getDate() + 1)
      .toString()
      .padStart(2, '0');
    ganttEvent.end = convertToFullCalendarDateString(
      `${ganttEventEndYear}/${ganttEventEndMonth}/${ganttEventEndDate}`
    );
  }

  // 色設定
  ganttEvent.textColor = getEventTextColor(siteProcessData);
  ganttEvent.backgroundColor = getEventBackgroundColor(siteProcessData);
  ganttEvent.borderColor = ganttEvent.backgroundColor;

  return ganttEvent;
};

// 検索期間の判定
export const validateGanttPeriod = (start: string, end: string) => {
  const endDate = new Date(end);
  const latestEndDate = new Date(start);
  latestEndDate.setFullYear(
    latestEndDate.getFullYear() + GANTT_DISPLAY_MAX_YEAR
  );

  return endDate <= latestEndDate;
};
